import { pDefer, request } from '~js/utils';
import { formatPrice, fetchProducts } from '~js/utils/products-and-collections';

const CART_GET_URL = window.Shopify.routes.root + 'cart.js';
const CART_ADD_URL = window.Shopify.routes.root + 'cart/add.js';
const CART_UPDATE_URL = window.Shopify.routes.root + 'cart/update.js';
const CART_CHANGE_URL = window.Shopify.routes.root + 'cart/change.js';

export const cartDataInitial = pDefer();

export const cartStore = () => ({
  cartData: null,
  giftBoxProductData: null,

  async init() {
    await this.fetchCart();
    cartDataInitial.resolve(this.cartData);

    const productData = await fetchProducts([window.GIFT_BOX_PRODUCT_ID]);
    this.giftBoxProductData = productData[window.GIFT_BOX_PRODUCT_ID];
  },
  /**
   * START: FIRST-CLASS ACTIONS
   * These interact with AJAX API and must set cartData
   * consequentially.
   */
  async fetchCart() {
    const url = new URL(window.location.href);
    url.pathname = CART_GET_URL;
    url.search = new URLSearchParams({
      t: Date.now(),
    });

    const params = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const data = await request(url, params);
    this.cartData = data;
  },

  async addToCart(body) {
    await request(CART_ADD_URL, {
      method: 'post',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    });

    this.fetchCart();
  },

  async updateCart(body) {
    const url = CART_UPDATE_URL;
    const params = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };

    const data = await request(url, params);
    // If only remaining item is Gift Box, remove that too
    if (data.items.length === 1 && data.items[0].product_id === window.GIFT_BOX_PRODUCT_ID && !this.clearingGiftBox) {
      // note, recursive
      // should only be entered once, clearingGiftBox is saftey flag against infinite loop
      this.clearingGiftBox = true;
      await this.removeLineItem(data.items[0].key);
      await this.clearGiftBoxAttributes();
      this.clearingGiftBox = false;
    } else {
      // normal case
      this.cartData = data;
    }
    this.clearingGiftBox = false;
  },

  async changeLineItem(body) {
    const url = CART_CHANGE_URL;
    const params = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };

    const data = await request(url, params);
    this.cartData = data;
  },

  /**
   * SECOND-CLASS ACTIONS
   * These call POST method FIRST-CLASS ACTIONS with predetermined bodies
   * They should not handle response data, but do return awaitable promises.
   */
  async removeLineItem(key) {
    return await this.changeLineItem({
      id: key,
      quantity: 0,
    });
  },

  async clearGiftBoxAttributes() {
    const cartAttributes = { ...this.cartData.attributes };
    cartAttributes['gift-wrapping'] = null;
    cartAttributes['gift-note'] = null;

    return await this.updateCart({
      attributes: cartAttributes,
    });
  },

  get normalizedCartQty() {
    return (
      this.cartData?.items
        .filter((item) => item.product_id !== window.GIFT_BOX_PRODUCT_ID)
        .reduce((acc, item) => acc + item.quantity, 0) || 0
    );
  },

  get lineItemsNormalized() {
    // currently normalization consists only of ensuring
    // that any gift box product is last in order

    return [...this.cartData.items].sort((a, b) =>
      a.product_id === window.GIFT_BOX_PRODUCT_ID ? 1 : b.product_id === window.GIFT_BOX_PRODUCT_ID ? -1 : 0,
    );
  },

  get giftBoxItemOnCart() {
    return this.cartData.items.find((item) => item.product_id === window.GIFT_BOX_PRODUCT_ID);
  },

  get giftBoxPrice() {
    const { giftBoxProductData } = this;
    if (giftBoxProductData) {
      return giftBoxProductData.variants[0].priceFormatted;
    }

    return '';
  },
});

export const cartComponent = () => ({
  get cartHasDiscounts() {
    return this.$store.cart.cartData.total_discount > 0;
  },

  get discountTotal() {
    if (this.$store.cart.cartData?.currency && this.$store.cart?.cartData.total_discount) {
      return formatPrice({
        currencyCode: this.$store.cart?.cartData.currency,
        amount: this.$store.cart?.cartData.total_discount ? this.$store.cart?.cartData.total_discount / -100 : 0,
      });
    } else return '';
  },

  get subtotal() {
    if (this.$store.cart.cartData?.currency) {
      return formatPrice({
        currencyCode: this.$store.cart.cartData.currency,
        amount: this.$store.cart.cartData.total_price ? this.$store.cart.cartData.total_price / 100 : 0,
      });
    } else {
      return '';
    }
  },
});
